import {
  API_HEADER,
  API_URL,
  USER_URL,
  userPublicPostsUrl,
} from "@modules/shared/enums/common-enum";
import type { UserPublicPost } from "@modules/profiles/models/user-public-post.model";
import {
  ClientDTO,
  TrainerDTO,
  UserEntity,
} from "@modules/shared/entities/users/users.entity";
import { UserRole } from "@modules/shared/enums/user.enum";
import { fetchWithAuth } from "@modules/shared/helpers/fetch-with-auth.helper";
import { AuthenticationEntity } from "@modules/auth/entities/auth.entity";

export class ProfileService {
  private getHeaders(): HeadersInit {
    return {
      ...API_HEADER,
      "Content-Type": "application/json",
    };
  }

  async getProfile(
    auth: AuthenticationEntity | null,
    setAuth: (auth: AuthenticationEntity | null) => void,
    logout: () => void,
    userId: string | "me",
  ) {
    const res = await fetchWithAuth(
      `${USER_URL}/${userId}`,
      {
        method: "GET",
        headers: this.getHeaders(),
      },
      auth,
      setAuth,
      logout,
    );

    const apiSocial = res.data.social;
    const user: UserEntity = {
      name: res.data.name,
      phoneNumber: res.data.phone_number,
      username: res.data.username,
      countryCode: res.data.country_code,
      fitCoins: res.data.fit_coins,
      email: res.data.email,
      status: res.data.status,
      role: res.data.role,
      skill: res.data.skills,
      avatar: res.data.avatars,
      fitnessGoal: res.data.fitness_goals,
      address: res.data.address,
      gender: res.data.gender,
      dateOfBirth: res.data.date_of_birth,
      bio: res.data.bio,
      weight: res.data.weight,
      height: res.data.height,
      id: res.data.id,
      verified: res.data.verified,
      adminLevel: res.data.role, // Never have admin with trainer role i believe. If that happen and cause to break, fix later
      social: apiSocial
        ? {
            facebook: apiSocial.facebook,
            instagram: apiSocial.instagram,
            tiktok: apiSocial.tiktok,
          }
        : undefined,
    };

    const client: ClientDTO = {
      ...user,
    };

    const trainer: TrainerDTO = {
      ...user,
      maxPrice: res.data.max_price,
      minPrice: res.data.min_price,
      subscription: res.data.subscription ?? res.data.trainer_subscription ?? null,
      expertise: res.data.expertise,
      fitnessLevel: res.data.fitness_level,
      certifications: res.data.certifications,
      availability: res.data.availability,
      experiences: res.data.experiences,
    };

    if (res.data.role === UserRole.TRAINER) {
      return trainer;
    } else if (
      res.data.role === UserRole.USER ||
      res.data.role === UserRole.ADMIN ||
      res.data.role === UserRole.SUPER_ADMIN ||
      res.data.role === UserRole.VERIFIED_PARTNER
    ) {
      return client;
    } else {
      console.error("Fail to get profile !");
      throw new Error("Error");
    }
  }

  async getBalanceWallet(
    auth: AuthenticationEntity | null,
    setAuth: (auth: AuthenticationEntity | null) => void,
    logout: () => void,
    userId: string,
  ) {
    const res = await fetchWithAuth(
      `${API_URL.GET_WALLET_OF_USER_ID}/${userId}`,
      {
        method: "GET",
        headers: this.getHeaders(),
      },
      auth,
      setAuth,
      logout,
    );

    return res.data?.[0]?.balance;
  }

  async getWeeklySchedule(
    auth: AuthenticationEntity | null,
    setAuth: (auth: AuthenticationEntity | null) => void,
    logout: () => void,
    userId: string,
  ) {
    const res = await fetchWithAuth(
      `${API_URL.WEEKLY_SCHEDULE}/${userId}`,
      {
        method: "GET",
        headers: this.getHeaders(),
      },
      auth,
      setAuth,
      logout,
    );
    return res.data;
  }

  /**
   * Paginated public posts for a user (social feed). Fetches until a page is empty or shorter than `limit`.
   */
  async getAllUserPublicPosts(
    auth: AuthenticationEntity | null,
    setAuth: (auth: AuthenticationEntity | null) => void,
    logout: () => void,
    userId: string,
    limit = 50,
  ): Promise<UserPublicPost[]> {
    const posts: UserPublicPost[] = [];
    let page = 1;
    const maxPages = 100;

    while (page <= maxPages) {
      const res = await fetchWithAuth(
        userPublicPostsUrl(userId, page, limit),
        {
          method: "GET",
          headers: this.getHeaders(),
        },
        auth,
        setAuth,
        logout,
      );

      const batch: UserPublicPost[] = res?.data ?? [];
      const total: number | undefined = res?.pagination?.total;

      if (!batch.length) break;

      posts.push(...batch);

      if (batch.length < limit) break;
      if (typeof total === "number" && total > 0 && posts.length >= total) break;

      page += 1;
    }

    return posts;
  }
}
